import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { ErrorBoundary } from 'react-error-boundary';
import {
  IoSearch,
  IoArrowBack,
  IoArrowForward,
  IoCheckmark,
  IoChevronDown,
} from 'react-icons/io5';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';

import BuilderErrorFallback from '../../components/errors/BuilderErrorFallback';
import PermissionModal from '../../components/PermissionModal';
import DestinationPartnerIcon from '../../components/icons/DestinationPartnerIcon';
import { useAuth } from '../../hooks/useAuth';
import { canUseActivations } from '../../utils';
import { PURCHASED_AUDIENCES_QUERY } from '../../queries/audienceQueries';
import { DESTINATION_PARTNERS_QUERY } from '../../queries/destinationQueries';
import { SAVE_ACTIVATION } from '../../mutations/activationMutation';
import { useActivation } from './ActivationContext';
import {
  ActivationActionTypes,
  Audience,
  DestinationPartner,
} from '../../reducers/activationBuilderReducer';
import {
  formatTimestamp,
  formatUserCount,
  formatDate,
} from './activationFormatters';

/* ============================================================================
 * ActivationBuilder — 3-step distribution flow.
 *
 *   1. Setup   — pick a single audience and one or more destinations on the
 *                same page (panels stacked vertically, each scrolling
 *                internally).
 *   2. Review  — preview-before-send page: shows the chosen audience and a
 *                card per selected destination. No knobs to turn — the
 *                distribution percentage is always 100% (slider removed). The
 *                user either goes back to edit or hits "Start Distribution".
 *   3. Confirm — shows the just-created activations.
 *
 * Every selected destination receives the default 100% allocation, handled
 * by the reducer's TOGGLE_DESTINATION case and by the fallback in
 * `handleStartDistribution`.
 *
 * Shared cross-step state still lives in ActivationContext so callers like
 * ActionsDropdown can deep-link to a specific audience via `?audID=…`.
 * ========================================================================== */

type Step = 'setup' | 'review' | 'confirm';

// Hardcoded connections for the mock feature — see notes in earlier
// ActivationBuilder. LiveRamp folder variants are all treated as connected.
const CONNECTED_SLUGS = [
  'google-ads',
  'tiktok-ads',
  'linkedin-ads',
  'amazon-dsp',
  'the-trade-desk',
  'snowflake',
  's3',
  'sftp',
];
const isConnectedSlug = (slug: string) =>
  CONNECTED_SLUGS.includes(slug) || slug.startsWith('liveramp-');

/* -------------------------------------------------------------------------- */
/* Entry point                                                                */
/* -------------------------------------------------------------------------- */

export default function ActivationBuilder() {
  const { user } = useAuth();
  const isActivationEnabled = canUseActivations(user);

  const { state, dispatch } = useActivation();
  const [step, setStep] = useState<Step>('setup');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    loading: audLoading,
    error: audError,
    data: audData,
  } = useQuery(PURCHASED_AUDIENCES_QUERY, { fetchPolicy: 'cache-and-network' });

  const {
    loading: destLoading,
    error: destError,
    data: destData,
  } = useQuery(DESTINATION_PARTNERS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const [saveActivation, { loading: saving }] = useMutation(SAVE_ACTIVATION);

  /* ---------- audience selection / deep-link handling ---------- */

  const selectAudience = (audience: Audience) => {
    dispatch({ type: ActivationActionTypes.SET_AUDIENCE, payload: audience });
    dispatch({ type: ActivationActionTypes.SET_STAGE });
  };

  useEffect(() => {
    const audID = searchParams.get('audID');
    if (audID) {
      const matched = audData?.purchasedAudiences.find((a) => a.id === audID);
      if (matched) {
        dispatch({
          type: ActivationActionTypes.SET_AUDIENCE,
          payload: matched,
        });
        searchParams.delete('audID');
        setSearchParams(searchParams, { replace: true });
      }
    } else if (
      !state.selectedAudience &&
      audData?.purchasedAudiences?.length > 0
    ) {
      dispatch({
        type: ActivationActionTypes.SET_AUDIENCE,
        payload: audData.purchasedAudiences[0],
      });
    }
  }, [
    state?.selectedAudience?.id,
    searchParams,
    audData?.purchasedAudiences,
    dispatch,
    setSearchParams,
  ]);

  /* ---------- destination toggling ---------- */

  const toggleDestination = (destination: DestinationPartner) => {
    dispatch({
      type: ActivationActionTypes.TOGGLE_DESTINATION,
      payload: destination,
    });
    dispatch({ type: ActivationActionTypes.SET_STAGE });
  };

  /* ---------- submission ---------- */

  const handleStartDistribution = async () => {
    if (!state.selectedAudience || state.selectedDestinations.length === 0) {
      return;
    }
    try {
      setSubmitError(null);

      const promises = state.selectedDestinations.map((destination) => {
        // Configure step is hidden; every destination gets the default 100%
        // allocation. The reducer also seeds `audienceDistribution[id] = 1`
        // when a destination is toggled on.
        const percentSelected =
          state.audienceDistribution[destination.id] ?? 1.0;
        const recordsIncluded = Math.floor(
          state.selectedAudience!.recordsIncluded * percentSelected
        );
        const processingId = crypto.randomUUID();

        const variables: Record<string, any> = {
          audienceId: state.selectedAudience!.id,
          audienceName: state.selectedAudience!.name,
          destinationPartnerId: destination.id,
          destinationPartnerName: destination.name,
          percentSelected,
          recordsIncluded,
          processingId,
        };
        // No s3Config / sftpConfig is sent — the credential UI was removed
        // with the configure step. Internal-delivery partners (LiveRamp
        // folder variants) don't need credentials and are unaffected.
        return saveActivation({ variables });
      });

      const results = await Promise.all(promises);
      const savedActivations = results.map((r) => r.data.saveActivation);
      dispatch({
        type: ActivationActionTypes.SAVE_ACTIVATIONS,
        payload: savedActivations,
      });
      setStep('confirm');
    } catch (err: any) {
      console.error('Error saving activations:', err);
      setSubmitError(
        err?.message || 'Something went wrong starting the distribution.'
      );
    }
  };

  /* ---------- restart for new distribution ---------- */

  const restart = () => {
    dispatch({ type: ActivationActionTypes.RESET });
    setSubmitError(null);
    setStep('setup');
  };

  /* ---------- render ---------- */

  let body: React.ReactNode;
  if (step === 'setup') {
    body = (
      <SetupStep
        audiences={audData?.purchasedAudiences || []}
        audLoading={audLoading}
        audError={!!audError}
        selectedAudienceId={state.selectedAudience?.id}
        onSelectAudience={selectAudience}
        destinations={destData?.destinationPartners || []}
        destLoading={destLoading}
        destError={!!destError}
        selectedDestinations={state.selectedDestinations}
        onToggleDestination={toggleDestination}
        onNext={() => {
          setSubmitError(null);
          setStep('review');
        }}
      />
    );
  } else if (step === 'review') {
    body = (
      <ReviewStep
        audience={state.selectedAudience}
        destinations={state.selectedDestinations}
        submitError={submitError}
        saving={saving}
        onBack={() => setStep('setup')}
        onChangeAudience={() => setStep('setup')}
        onStart={handleStartDistribution}
      />
    );
  } else {
    body = (
      <ConfirmStep
        audienceName={state.selectedAudience?.name || ''}
        recentActivations={state.recentActivations}
        onRestart={restart}
      />
    );
  }

  return (
    <>
      <ErrorBoundary
        FallbackComponent={BuilderErrorFallback}
        onReset={() => {}}
      >
        <div className="min-h-[calc(100vh-40px)] bg-gray-50 pb-16">{body}</div>
      </ErrorBoundary>

      {!isActivationEnabled && <PermissionModal />}
    </>
  );
}

/* ========================================================================== */
/* Setup step — combined audience + destinations                              */
/* ========================================================================== */

function SetupStep({
  audiences,
  audLoading,
  audError,
  selectedAudienceId,
  onSelectAudience,
  destinations,
  destLoading,
  destError,
  selectedDestinations,
  onToggleDestination,
  onNext,
}: {
  audiences: Audience[];
  audLoading: boolean;
  audError: boolean;
  selectedAudienceId: string | undefined;
  onSelectAudience: (a: Audience) => void;
  destinations: DestinationPartner[];
  destLoading: boolean;
  destError: boolean;
  selectedDestinations: DestinationPartner[];
  onToggleDestination: (d: DestinationPartner) => void;
  onNext: () => void;
}) {
  const selectedDestIds = selectedDestinations.map((d) => d.id);
  const canAdvance =
    !!selectedAudienceId && selectedDestIds.length > 0;

  return (
    <div className="mx-auto max-w-[720px] px-12 pb-12 pt-9">
      <div className="mb-6">
        <h1 className="mb-2 text-[2rem] font-extrabold tracking-tight text-navy">
          Activate Audiences
        </h1>
        <p className="m-0 max-w-[46ch] text-[1.05rem] leading-snug text-gray-500">
          Distribute your custom audiences to key advertising &amp; analytics
          platforms.
        </p>
      </div>

      <div className="flex flex-col gap-5">
        {/* --- Audience picker (searchable dropdown) --- */}
        <Panel title="Select Audience">
          <AudienceDropdown
            audiences={audiences}
            selectedId={selectedAudienceId}
            onSelect={onSelectAudience}
            loading={audLoading}
            error={audError}
          />
        </Panel>

        {/* --- Destination picker --- */}
        <Panel title="Choose Destinations">
          <div className="flex max-h-[320px] flex-col gap-2.5 overflow-y-auto pr-1">
            {destLoading && (
              <p className="m-0 py-4 text-center text-sm text-gray-500">
                Loading destinations…
              </p>
            )}
            {!destLoading && !destError && destinations.length === 0 && (
              <p className="m-0 py-4 text-center text-sm text-gray-500">
                No destinations found.
              </p>
            )}
            {!destLoading &&
              !destError &&
              destinations.map((d) => {
                const on = selectedDestIds.includes(d.id);
                const connected = isConnectedSlug(d.slug);
                return (
                  <div
                    key={d.id}
                    role={connected ? 'button' : undefined}
                    onClick={() => connected && onToggleDestination(d)}
                    className={`flex w-full items-center justify-between gap-3 rounded-[11px] border-[1.5px] px-4 py-3.5 transition ${
                      on
                        ? 'border-green bg-[#E7F4F2]'
                        : 'border-gray-200 bg-white'
                    } ${
                      connected
                        ? 'cursor-pointer hover:border-green'
                        : 'cursor-default'
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-3.5">
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[9px] bg-[#F1F5F8] text-navy">
                        <DestinationPartnerIcon slug={d.slug} size={28} />
                      </span>
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="text-[1.02rem] font-semibold text-navy">
                          {d.name}
                        </span>
                        <span className="truncate text-xs text-gray-500">
                          {d.displayName
                            ? d.displayName
                            : connected
                              ? 'Connected'
                              : 'Not connected'}
                        </span>
                      </span>
                    </span>
                    {connected ? (
                      <CheckboxMark on={on} />
                    ) : (
                      <Link to={`./connectdestination/${d.slug}`}>
                        <button
                          type="button"
                          onClick={(e) => e.stopPropagation()}
                          className="rounded-[9px] border-[1.5px] border-gray-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-gray-900 transition hover:border-gray-400 hover:bg-gray-50"
                        >
                          Connect
                        </button>
                      </Link>
                    )}
                  </div>
                );
              })}
          </div>
        </Panel>
      </div>

      <div className="mt-7 flex items-center justify-between gap-4">
        <span className="text-sm text-gray-500">
          {!selectedAudienceId
            ? 'Pick an audience and at least one destination'
            : selectedDestIds.length === 0
              ? 'Select at least one destination'
              : `${selectedDestIds.length} destination${
                  selectedDestIds.length > 1 ? 's' : ''
                } selected`}
        </span>
        <PrimaryButton large disabled={!canAdvance} onClick={onNext}>
          Next: Review <IoArrowForward size={17} />
        </PrimaryButton>
      </div>
    </div>
  );
}

/* ========================================================================== */
/* Review step — preview before send                                           */
/* ========================================================================== */

function ReviewStep({
  audience,
  destinations,
  submitError,
  saving,
  onBack,
  onChangeAudience,
  onStart,
}: {
  audience: Audience | null;
  destinations: DestinationPartner[];
  submitError: string | null;
  saving: boolean;
  onBack: () => void;
  onChangeAudience: () => void;
  onStart: () => void;
}) {
  return (
    <div className="mx-auto max-w-[720px] px-12 pb-12 pt-9">
      <div className="mb-6 flex items-center gap-3.5">
        <BackButton onClick={onBack} />
        <h1 className="m-0 text-[1.7rem] font-extrabold text-navy">
          Review Distribution
        </h1>
      </div>

      <div className="mb-5">
        <AudienceRecap audience={audience} onChange={onChangeAudience} />
      </div>

      <div className="flex flex-col gap-4">
        {destinations.map((d) => (
          <ReviewCard
            key={d.id}
            destination={d}
            // Distribution is fixed at 100% of the audience — the slider was
            // removed from the UI per product direction.
            audienceSize={audience?.recordsIncluded || 0}
          />
        ))}
      </div>

      {submitError && (
        <div className="mt-4 rounded-[9px] border border-error/30 bg-error/10 p-4">
          <p className="m-0 text-sm text-error">{submitError}</p>
        </div>
      )}

      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded-[9px] border-[1.5px] border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 transition hover:border-gray-400 hover:bg-gray-50"
        >
          Cancel
        </button>
        <PrimaryButton onClick={onStart} disabled={saving}>
          {saving ? 'Starting…' : 'Start Distribution'}
        </PrimaryButton>
      </div>
    </div>
  );
}

function ReviewCard({
  destination,
  audienceSize,
}: {
  destination: DestinationPartner;
  audienceSize: number;
}) {
  return (
    <div className="rounded-xl border-[1.5px] border-gray-200 bg-white p-6 shadow-[0_1px_2px_rgba(15,34,51,0.05)]">
      <div className="flex items-center gap-3.5">
        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[11px] bg-[#F1F5F8] text-navy">
          <DestinationPartnerIcon slug={destination.slug} size={32} />
        </span>
        <div>
          <h3 className="m-0 text-[1.18rem] font-bold text-navy">
            {destination.name}
          </h3>
          {destination.displayName && (
            <p className="m-0 mt-0.5 text-xs text-gray-500">
              {destination.displayName}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-[9px] bg-[#F1F5F8] px-4 py-3 text-sm text-gray-500">
        <strong className="mr-1.5 text-gray-900">Audience size:</strong>
        {audienceSize.toLocaleString('en-US')} users
      </div>
    </div>
  );
}

/* ========================================================================== */
/* AudienceRecap + BackButton                                                  */
/* ========================================================================== */

function AudienceRecap({
  audience,
  onChange,
}: {
  audience: Audience | null;
  onChange: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-[11px] border-[1.5px] border-green bg-[#E7F4F2] px-[18px] py-3">
      <span className="text-[0.7rem] font-bold uppercase tracking-wider text-green-dark">
        Audience
      </span>
      <span className="font-bold text-navy">{audience?.name}</span>
      <span className="text-xs text-gray-500">
        {formatUserCount(audience?.recordsIncluded || 0)}
      </span>
      <button
        type="button"
        onClick={onChange}
        className="ml-auto border-none bg-transparent text-xs font-semibold text-green-dark hover:underline"
      >
        Change
      </button>
    </div>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 border-none bg-transparent text-sm font-medium text-gray-500 transition hover:text-green"
    >
      <IoArrowBack size={18} /> Back
    </button>
  );
}

/* ========================================================================== */
/* AudienceDropdown — searchable combobox                                      */
/* ========================================================================== */

function AudienceDropdown({
  audiences,
  selectedId,
  onSelect,
  loading,
  error,
}: {
  audiences: Audience[];
  selectedId: string | undefined;
  onSelect: (a: Audience) => void;
  loading: boolean;
  error: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = audiences.find((a) => a.id === selectedId);

  const filtered = useMemo(
    () =>
      audiences.filter((a) =>
        a.name.toLowerCase().includes(q.toLowerCase())
      ),
    [audiences, q]
  );

  // Close on outside click + Escape; focus the search box when opening so the
  // user can start typing immediately.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    // Slight delay so the focus doesn't fight with the toggle click.
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
  }, [open]);

  // Reset search whenever the popover closes — keeps the next open fresh.
  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const handlePick = (a: Audience) => {
    onSelect(a);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center justify-between gap-3 rounded-[9px] border-[1.5px] bg-white px-4 py-3 text-left transition ${
          open
            ? 'border-green shadow-[0_0_0_3px_rgba(37,159,147,0.30)]'
            : 'border-gray-200 hover:border-green'
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex min-w-0 flex-col gap-0.5">
          {selected ? (
            <>
              <span className="truncate text-[1.02rem] font-semibold text-navy">
                {selected.name}
              </span>
              <span className="truncate text-xs text-gray-500">
                {formatUserCount(selected.recordsIncluded)} · Last updated:{' '}
                {formatDate(selected.purchasedAt)}
              </span>
            </>
          ) : (
            <span className="text-sm text-gray-500">
              {loading ? 'Loading audiences…' : 'Select an audience…'}
            </span>
          )}
        </span>
        <IoChevronDown
          size={18}
          className={`flex-shrink-0 text-gray-500 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Popover */}
      {open && (
        <div
          className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-[11px] border-[1.5px] border-gray-200 bg-white shadow-[0_8px_24px_rgba(15,34,51,0.12)]"
          role="listbox"
        >
          <div className="relative border-b border-gray-100 p-3">
            <IoSearch
              className="pointer-events-none absolute left-6 top-1/2 -translate-y-1/2 text-gray-400"
              size={16}
            />
            <input
              ref={searchRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search audiences…"
              className="w-full rounded-[9px] border-[1.5px] border-gray-200 py-2 pl-9 pr-3 text-sm text-gray-900 outline-none transition focus:border-green focus:shadow-[0_0_0_3px_rgba(37,159,147,0.30)]"
            />
          </div>

          <div className="flex max-h-[280px] flex-col gap-1.5 overflow-y-auto p-3">
            {loading && (
              <p className="m-0 py-3 text-center text-sm text-gray-500">
                Loading your audiences…
              </p>
            )}
            {!loading && error && (
              <p className="m-0 py-3 text-center text-sm text-error">
                Couldn’t load audiences.
              </p>
            )}
            {!loading && !error && filtered.length === 0 && (
              <p className="m-0 py-3 text-center text-sm text-gray-500">
                No audiences found.
              </p>
            )}
            {!loading &&
              !error &&
              filtered.map((a) => {
                const on = a.id === selectedId;
                return (
                  <button
                    key={a.id}
                    type="button"
                    role="option"
                    aria-selected={on}
                    onClick={() => handlePick(a)}
                    className={`flex w-full items-center justify-between gap-3 rounded-[9px] border-[1.5px] px-3 py-2.5 text-left transition ${
                      on
                        ? 'border-green bg-[#E7F4F2]'
                        : 'border-transparent bg-white hover:border-green hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate text-sm font-semibold text-navy">
                        {a.name}
                      </span>
                      <span className="truncate text-xs text-gray-500">
                        {formatUserCount(a.recordsIncluded)} · Last updated:{' '}
                        {formatDate(a.purchasedAt)}
                      </span>
                    </span>
                    {on && (
                      <IoCheckmark
                        size={16}
                        strokeWidth={40}
                        className="flex-shrink-0 text-green"
                      />
                    )}
                  </button>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ========================================================================== */
/* Confirm step                                                                */
/* ========================================================================== */

function ConfirmStep({
  audienceName,
  recentActivations,
  onRestart,
}: {
  audienceName: string;
  recentActivations: any[];
  onRestart: () => void;
}) {
  if (!recentActivations || recentActivations.length === 0) {
    return (
      <div className="flex min-h-[calc(100vh-40px)] justify-center bg-gray-50 px-4 py-10">
        <div className="w-full max-w-3xl rounded-xl bg-white p-10 shadow-sm">
          <h1 className="m-0 text-[1.625rem] font-bold text-gray-900">
            No Activations Found
          </h1>
          <button
            type="button"
            onClick={onRestart}
            className="mt-4 text-blue hover:underline"
          >
            Start a new distribution
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[720px] px-12 pb-12 pt-9">
      <div className="rounded-xl border-[1.5px] border-gray-200 bg-white p-9 shadow-[0_1px_2px_rgba(15,34,51,0.05)]">
        <div className="flex items-center justify-between border-b border-gray-200 pb-4">
          <span className="flex items-center gap-3">
            <h1 className="m-0 text-[1.5rem] font-extrabold text-navy">
              Distribution Confirmation
            </h1>
            <IoIosCheckmarkCircleOutline className="text-3xl text-green-light" />
          </span>
          <Link
            to="/dashboard/mywork/distributions"
            className="text-xs font-semibold text-gray-500 hover:text-green hover:underline"
          >
            View Details
          </Link>
        </div>

        <div className="mb-2 mt-6 border-b border-gray-100 pb-3.5">
          <p className="m-0 text-xs text-gray-500">Audience Name</p>
          <p className="m-0 mt-1 text-[1.3rem] font-bold text-navy">
            {audienceName}
          </p>
        </div>

        <div className="mt-5 flex flex-col gap-3.5">
          {recentActivations.map((act) => (
            <ConfirmBlock
              key={act.id}
              slug={act.destinationPartner.slug}
              name={act.destinationPartner.name}
              displayName={act.destinationPartner.displayName}
              audienceSize={act.recordsIncluded}
              startedAt={act.startedAt}
            />
          ))}
        </div>

        <div className="mt-7 flex justify-end">
          <button
            type="button"
            onClick={onRestart}
            className="rounded-[9px] border-[1.5px] border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 transition hover:border-gray-400 hover:bg-gray-50"
          >
            New Distribution
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmBlock({
  slug,
  name,
  displayName,
  audienceSize,
  startedAt,
}: {
  slug: string;
  name: string;
  displayName?: string | null;
  audienceSize: number;
  startedAt: any;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-[11px] border-[1.5px] border-gray-200 px-5 py-4.5">
      <span className="flex items-center gap-3.5">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[9px] bg-[#F1F5F8] text-navy">
          <DestinationPartnerIcon slug={slug} size={28} />
        </span>
        <span className="flex flex-col gap-0.5">
          <span className="text-[1.02rem] font-semibold text-navy">{name}</span>
          {displayName && (
            <span className="text-xs text-gray-500">{displayName}</span>
          )}
        </span>
      </span>
      <div className="text-right text-sm">
        <p className="m-0 mb-0.5 whitespace-nowrap">
          <span className="text-gray-500">Audience Size: </span>
          <span className="font-medium text-gray-900">
            {audienceSize.toLocaleString('en-US')} users
          </span>
        </p>
        <p className="m-0 whitespace-nowrap">
          <span className="text-gray-500">Started: </span>
          <span className="font-medium text-gray-900">
            {formatTimestamp(startedAt) || '—'}
          </span>
        </p>
      </div>
    </div>
  );
}

/* ========================================================================== */
/* Shared atoms                                                                */
/* ========================================================================== */

function Panel({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border-[1.5px] border-gray-200 bg-white p-6 shadow-[0_1px_2px_rgba(15,34,51,0.05)]">
      <h2 className="m-0 mb-4 text-[1.12rem] font-bold text-navy">{title}</h2>
      {children}
    </section>
  );
}

function PrimaryButton({
  children,
  large = false,
  disabled = false,
  onClick,
}: {
  children: React.ReactNode;
  large?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-2 rounded-[9px] border-[1.5px] font-semibold transition ${
        large ? 'px-6 py-3 text-base' : 'px-4 py-2.5 text-sm'
      } ${
        disabled
          ? 'cursor-not-allowed border-gray-200 bg-gray-200 text-gray-400'
          : 'border-green bg-green text-white hover:border-green-dark hover:bg-green-dark'
      }`}
    >
      {children}
    </button>
  );
}

function CheckboxMark({ on }: { on: boolean }) {
  return (
    <span
      className={`flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-md border-2 text-white transition ${
        on ? 'border-green bg-green' : 'border-gray-300 bg-white'
      }`}
    >
      {on && <IoCheckmark size={14} strokeWidth={40} />}
    </span>
  );
}

/* Helpers live in ./activationFormatters and are imported at the top. */
